import { useEffect, useState } from "react";
import {
  FaFlag,
  FaCoins,
  FaPen,
  FaHandshake,
  FaHandshakeSlash,
  FaPaperPlane,
  FaCheck,
  FaXmark,
  FaGavel,
  FaDoorOpen,
  FaCirclePause,
  FaCirclePlay,
  FaLockOpen,
  FaHourglassHalf,
  FaFileCircleQuestion,
  FaCheckDouble,
  FaRotateLeft,
  FaTriangleExclamation,
  FaSpinner,
  FaFlagCheckered,
  
  FaBan,
  FaClockRotateLeft,
} from "react-icons/fa6";

/** One entry per real market_events.event_type — see
 *  packages/domain/src/markets/marketService.ts for the authoritative list of what gets
 *  recorded. Unknown/future event types fall back to a generic entry rather than being
 *  silently dropped, so a new event type shows up (plainly labeled) the moment it exists
 *  server-side, without needing this map updated in lockstep. */
const EVENT_META = {
  MARKET_CREATED: { icon: FaFlag, tone: "brand", title: "Market created" },
  MARKET_CREATION_CHARGED: { icon: FaCoins, tone: "amber", title: "Creation fee charged" },
  MARKET_CREATION_CHARGE_FAILED: { icon: FaTriangleExclamation, tone: "red", title: "Creation charge failed — cancelled" },
  MARKET_UPDATED: { icon: FaPen, tone: "gray", title: "Details updated" },
  MARKET_CLIENT_ASSIGNED: { icon: FaHandshake, tone: "brand", title: "Assigned to a partner" },
  MARKET_SUBMITTED_FOR_APPROVAL: { icon: FaPaperPlane, tone: "gray", title: "Submitted for approval" },
  MARKET_APPROVED: { icon: FaCheck, tone: "green", title: "Approved" },
  MARKET_APPROVAL_REJECTED: { icon: FaXmark, tone: "red", title: "Rejected — sent back to draft" },
  MARKET_AUCTION_STARTED: { icon: FaGavel, tone: "amber", title: "Opening auction started" },
  MARKET_OPENED: { icon: FaDoorOpen, tone: "green", title: "Opened for trading" },
  MARKET_SUSPENDED: { icon: FaCirclePause, tone: "amber", title: "Suspended" },
  MARKET_RESUMED: { icon: FaCirclePlay, tone: "green", title: "Resumed" },
  MARKET_CLOSED: { icon: FaLockOpen, tone: "gray", title: "Closed to trading" },
  MARKET_AWAITING_RESULT: { icon: FaHourglassHalf, tone: "amber", title: "Awaiting result" },
  MARKET_RESULT_PROPOSED: { icon: FaFileCircleQuestion, tone: "brand", title: "Result proposed" },
  MARKET_RESULT_APPROVED: { icon: FaCheckDouble, tone: "green", title: "Result approved" },
  MARKET_RESULT_REJECTED: { icon: FaRotateLeft, tone: "red", title: "Result rejected — back to awaiting result" },
  MARKET_RESULT_DISPUTED: { icon: FaTriangleExclamation, tone: "red", title: "Result disputed" },
  MARKET_SETTLEMENT_STARTED: { icon: FaSpinner, tone: "brand", title: "Settlement started" },
  MARKET_SETTLED: { icon: FaFlagCheckered, tone: "green", title: "Settled" },
  MARKET_CANCELLED: { icon: FaBan, tone: "red", title: "Cancelled" },
};
const UNASSIGN_META = { icon: FaHandshakeSlash, tone: "gray", title: "Unassigned from partner" };

const TONE_CLASSES = {
  brand: "bg-brand-50 text-brand-500 dark:bg-brand-400/20",
  green: "bg-green-50 text-green-600 dark:bg-green-400/10",
  red: "bg-red-50 text-red-500 dark:bg-red-400/10",
  amber: "bg-amber-50 text-amber-600 dark:bg-amber-400/10",
  gray: "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300",
};

function relativeTime(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const s = Math.max(0, Math.floor(diffMs / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function describe(event, currentUserId) {
  const meta =
    event.eventType === "MARKET_CLIENT_ASSIGNED" && event.payload?.clientId === null
      ? UNASSIGN_META
      : EVENT_META[event.eventType] || { icon: FaFlag, tone: "gray", title: event.eventType.replaceAll("_", " ").toLowerCase() };

  const p = event.payload || {};
  let detail = null;
  if (event.eventType === "MARKET_RESULT_PROPOSED" && p.proposedOutcomeId) {
    detail = "Awaiting a second reviewer's approval";
  } else if ((event.eventType === "MARKET_APPROVAL_REJECTED" || event.eventType === "MARKET_RESULT_REJECTED") && p.reason) {
    detail = p.reason;
  } else if (event.eventType === "MARKET_RESULT_DISPUTED" && p.disputeReason) {
    detail = p.disputeReason;
  }

  const actor = event.actorUserId === null ? "System" : event.actorUserId === currentUserId ? "You" : "A team member";
  return { ...meta, detail, actor };
}

/** A market's full activity timeline — every lifecycle transition
 *  event, and result decision, newest first. Shared design between the superadmin and
 *  partner portals (each keeps its own copy, matching this codebase's existing
 *  per-portal component convention) — only the fetch URL differs. */
export default function MarketTimeline({ fetchUrl, currentUserId, apiGet }) {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  function load(page = 1, append = false) {
    if (append) setLoadingMore(true);
    setError(null);
    apiGet(`${fetchUrl}${fetchUrl.includes("?") ? "&" : "?"}page=${page}&pageSize=${PAGE_SIZE}`)
      .then((res) => {
        setEvents((prev) => (append ? [...(prev || []), ...(res.events || [])] : res.events || []));
        setTotal(res.total || 0);
      })
      .catch((err) => setError(err.message || "Failed to load this market's activity."))
      .finally(() => setLoadingMore(false));
  }

  useEffect(() => {
    setEvents(null);
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUrl]);

  if (events === null && !error) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-md bg-accent" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-600 dark:border-red-400/20 dark:bg-red-400/10">
        {error} <button onClick={() => load(1, false)} className="ml-2 font-semibold underline">Retry</button>
      </div>
    );
  }

  if (events.length === 0) {
    return <p className="py-6 text-center text-sm text-gray-500">No activity recorded yet.</p>;
  }

  return (
    <div>
      <ol className="relative border-l-2 border-gray-100 pl-6 dark:border-white/10">
        {events.map((event) => {
          const { icon: Icon, tone, title, detail, actor } = describe(event, currentUserId);
          return (
            <li key={event.id} className="mb-6 last:mb-0">
              <span
                className={`absolute -left-[15px] flex h-7 w-7 items-center justify-center rounded-full text-xs ring-4 ring-white dark:ring-navy-800 ${TONE_CLASSES[tone]}`}
              >
                <Icon />
              </span>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="text-sm font-semibold text-navy-700 dark:text-white">{title}</p>
                <time className="text-xs text-gray-400" title={new Date(event.createdAt).toLocaleString()}>
                  {relativeTime(event.createdAt)}
                </time>
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                {actor}
                {detail ? ` · ${detail}` : ""}
              </p>
            </li>
          );
        })}
      </ol>
      {events.length < total && (
        <button
          onClick={() => load(Math.floor(events.length / PAGE_SIZE) + 1, true)}
          disabled={loadingMore}
          className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-brand-500 disabled:opacity-50"
        >
          <FaClockRotateLeft /> {loadingMore ? "Loading…" : "Load earlier activity"}
        </button>
      )}
    </div>
  );
}
